/**
 * useSubjectScope — DRY template for subject-scoped primitive views (DRR-141).
 *
 * Encapsulates all state management and data fetching for the subject-filter
 * pattern: terminology-aware subject dropdown + lifecycle phase badges +
 * constellation context panel.
 *
 * ## Usage
 *
 * ```tsx
 * const scope = useSubjectScope();
 * // Then: <SubjectFilterHeader scope={scope} />
 * //        <TabulatorPrimitiveManager overrideData={scope.subjectTasksOverride} />
 * ```
 *
 * ## DRY template
 *
 * This hook is the single source of truth for subject-filter logic. Updating
 * it benefits every primitive page that uses it (Tasks, Instances, Documents…).
 *
 * ## Data sources
 *
 * 1. `listSubjectsWithLifecycle` (DRR-141, DRR-140) — subjects + lifecycle phase
 *    from wf-0-12-001 instance current_state, role-scoped by DRR-135 Valkey traversal.
 * 2. `listActors` fallback — if lifecycle query returns empty, falls back to
 *    `GqlActorType.SUBJECT` actors (same as before DRR-141; safe degradation).
 * 3. Terminology — DRR-189 `useFhirLabel()` from @ferp/subgraph-ui context.
 * 4. URL param — `?subject=` pre-selects a subject (DRR-135 deep-link).
 * 5. Constellation — `useLiveConstellation` loads care team when a subject is selected.
 */

import { useState, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSearchParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { useFhirLabel } from '@ferp/subgraph-ui';
import { useLiveConstellation, type UseLiveConstellationReturn } from './useLiveConstellation';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

/**
 * listSubjectsWithLifecycle — DRR-141 backend resolver.
 * Returns SUBJECT actors with their lifecycle phase from wf-0-12-001 Instance.current_state.
 * Role-scoped by DRR-135 Valkey traversal (my_subject_actor_ids).
 */
const LIST_SUBJECTS_WITH_LIFECYCLE_QUERY = gql`
  query ListSubjectsWithLifecycle {
    listSubjectsWithLifecycle {
      actorId
      lifecyclePhase
    }
  }
`;

/** Fallback: all actors — client-side filter to SUBJECT type. */
const LIST_ACTORS_QUERY = gql`
  query ListActorsForSubjectScope($limit: Int!) {
    listActors(limit: $limit) {
      items {
        id
        name
        actorType
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Inline query data shapes
// (workflow-forms has no access to portal's generated/graphql.ts)
// ---------------------------------------------------------------------------

interface SubjectWithLifecycleItem {
  actorId: string;
  lifecyclePhase: string | null;
}

interface ListSubjectsWithLifecycleData {
  listSubjectsWithLifecycle: SubjectWithLifecycleItem[];
}

interface ActorItem {
  id: string;
  name: string;
  actorType: string;
}

interface ListActorsData {
  listActors: { items: ActorItem[] };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Lifecycle phase → Mantine badge color (used by SubjectFilterHeader). */
export const LIFECYCLE_PHASE_COLORS: Record<string, string> = {
  onboarding: 'yellow',
  active:     'green',
  exiting:    'orange',
  closed:     'gray',
};

// ---------------------------------------------------------------------------
// TabulatorFilterDefinition — mirrors the type in TabulatorPrimitiveManager
// so workflow-forms doesn't depend on portal internals.
// ---------------------------------------------------------------------------

/** A column filter applied to the Tabulator table via its `filterBy` prop. */
export interface TabulatorFilterDefinition {
  /** Tabulator column field name */
  field: string;
  /** Value to set in the column's header filter */
  value: string;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Filter mode for the primitive list. */
export type SubjectScopeFilter = 'all' | 'my' | 'subject';

/** A subject option for the dropdown, with optional lifecycle phase badge. */
export interface SubjectOption {
  /** Actor sequential ID (e.g., "17_42") */
  value: string;
  /** Display name (resolved from ActorNameContext or raw ID as fallback) */
  label: string;
  /** Current lifecycle phase: "onboarding" | "active" | "exiting" | "closed" | undefined */
  lifecyclePhase?: string;
}

export interface UseSubjectScopeProps {
  /**
   * Function to resolve an actor ID to a display name.
   * Use `useResolveActorName()` from ActorNameContext.
   * If omitted, actor IDs are shown as-is.
   */
  nameResolver?: (actorId: string) => string;
  /**
   * Whether to show only active subjects (onboarding/active/exiting, not closed).
   * Defaults to true. Pass false to show all subjects including CLOSED lifecycle.
   */
  activeOnly?: boolean;
  /**
   * DRR-256: When true and filter is 'all', groups tasks by subject actor ID.
   * Enables care staff to see tasks organized per resident.
   * Defaults to false.
   */
  groupBySubjectWhenAll?: boolean;
}

export interface UseSubjectScopeReturn {
  /** Currently selected filter tab */
  filter: SubjectScopeFilter;
  setFilter: (f: SubjectScopeFilter) => void;

  /** Currently selected subject actor ID */
  selectedSubjectId: string | null;
  setSelectedSubjectId: (id: string | null) => void;

  /** Subject dropdown options with lifecycle phase */
  subjectOptions: SubjectOption[];
  subjectsLoading: boolean;

  /** Display label for the subject tab (resolved via DRR-189 terminology) */
  subjectLabel: string;

  /** Display name of the selected subject */
  selectedSubjectName: string;

  /** Lifecycle phase of the selected subject (if known) */
  selectedSubjectPhase: string | undefined;

  /** Constellation state for the selected subject */
  constellation: UseLiveConstellationReturn['constellation'];
  constellationLoading: boolean;
  constellationOpen: boolean;
  toggleConstellation: () => void;

  /**
   * Derived group-by field based on the current filter state.
   * Pass directly to `TabulatorPrimitiveManager` `groupBy` prop.
   *
   * Rules:
   * - `filter === 'subject'` + subject selected → `'lifecyclePhase'`
   * - all other states → `null`
   */
  derivedGroupBy: string | null;

  /**
   * Derived column filters based on the current filter state.
   * Pass directly to `TabulatorPrimitiveManager` `filterBy` prop.
   *
   * Currently empty — reserved for future scope-driven filters
   * (e.g., "My Tasks" → status=IN_PROGRESS).
   */
  derivedFilterBy: TabulatorFilterDefinition[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSubjectScope(props: UseSubjectScopeProps = {}): UseSubjectScopeReturn {
  const { nameResolver, activeOnly = true, groupBySubjectWhenAll = false } = props;

  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get('subject');

  const [filter, setFilter] = useState<SubjectScopeFilter>(
    subjectParam ? 'subject' : 'all',
  );
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(subjectParam);
  const [constellationOpen, { toggle: toggleConstellation }] = useDisclosure(true);

  const handleSetFilter = (f: SubjectScopeFilter) => {
    setFilter(f);
    if (f !== 'subject') setSelectedSubjectId(null);
  };

  // ── Backend terminology (DRR-189) ────────────────────────────────────────
  // Uses FhirLabelsContext from @ferp/subgraph-ui, provided by portal's
  // FhirLabelsProvider. Returns industry-specific term for FHIR resource types.
  const getFhirLabel = useFhirLabel();
  const subjectLabel: string = getFhirLabel('Patient');

  // ── Subjects with lifecycle — DRR-141 preferred path ────────────────────
  const isSubjectTab = filter === 'subject';
  const { data: lifecycleData, loading: lifecycleLoading } = useQuery<ListSubjectsWithLifecycleData>(
    LIST_SUBJECTS_WITH_LIFECYCLE_QUERY,
    { skip: !isSubjectTab },
  );

  // ── Fallback: listActors — used when lifecycle query returns empty ────────
  const hasLifecycleResults =
    Array.isArray(lifecycleData?.listSubjectsWithLifecycle) &&
    lifecycleData.listSubjectsWithLifecycle.length > 0;

  const { data: actorData, loading: actorLoading } = useQuery<ListActorsData>(LIST_ACTORS_QUERY, {
    variables: { limit: 200 },
    skip: !isSubjectTab || hasLifecycleResults,
  });

  // ── Build subject options ────────────────────────────────────────────────
  const subjectOptions: SubjectOption[] = useMemo(() => {
    const resolve = nameResolver ?? ((id: string) => id);

    if (hasLifecycleResults) {
      const subjects = lifecycleData.listSubjectsWithLifecycle;
      return subjects
        .filter((s) => {
          if (!activeOnly) return true;
          // Show onboarding/active/exiting — hide closed
          return s.lifecyclePhase !== 'closed';
        })
        .map((s) => ({
          value: s.actorId,
          label: resolve(s.actorId),
          lifecyclePhase: s.lifecyclePhase ?? undefined,
        }));
    }

    // Fallback: listActors
    const items = actorData?.listActors?.items ?? [];
    return items
      .filter((a: { actorType: string }) => a.actorType === 'SUBJECT')
      .map((a: { id: string; name: string }) => ({
        value: a.id,
        label: a.name || resolve(a.id),
      }));
  }, [lifecycleData, actorData, hasLifecycleResults, nameResolver, activeOnly]);

  const subjectsLoading = lifecycleLoading || actorLoading;

  // ── Selected subject display ─────────────────────────────────────────────
  const resolve = nameResolver ?? ((id: string) => id);

  const selectedSubjectName = useMemo(() => {
    if (!selectedSubjectId) return '';
    return (
      subjectOptions.find((o) => o.value === selectedSubjectId)?.label ||
      resolve(selectedSubjectId)
    );
  }, [subjectOptions, selectedSubjectId, resolve]);

  const selectedSubjectPhase = useMemo(() => {
    if (!selectedSubjectId) return undefined;
    return subjectOptions.find((o) => o.value === selectedSubjectId)?.lifecyclePhase;
  }, [subjectOptions, selectedSubjectId]);

  // ── Constellation for selected subject ──────────────────────────────────
  const { constellation, isLoading: constellationLoading } = useLiveConstellation({
    centerId: selectedSubjectId ?? '',
    centerName: selectedSubjectName,
    skip: filter !== 'subject' || !selectedSubjectId,
    nameResolver: nameResolver,
  });

  // ── Derived tabulator controls ───────────────────────────────────────────
  // Group by lifecycle phase when a subject is selected — pages no longer
  // need to inline this expression; updating here benefits all pages.
  // DRR-256: Group by subject actor ID when viewing all tasks (person-centered view).
  const derivedGroupBy: string | null =
    filter === 'all' && groupBySubjectWhenAll
      ? 'subjectActorId' // DRR-256: Group all tasks by resident
      : filter === 'subject' && selectedSubjectId
      ? 'lifecyclePhase'
      : null;

  // Reserved: future scope-driven column filters go here
  // (e.g., filter === 'my' → [{ field: 'status', value: 'IN_PROGRESS' }])
  const derivedFilterBy: TabulatorFilterDefinition[] = [];

  return {
    filter,
    setFilter: handleSetFilter,
    selectedSubjectId,
    setSelectedSubjectId,
    subjectOptions,
    subjectsLoading,
    subjectLabel,
    selectedSubjectName,
    selectedSubjectPhase,
    constellation,
    constellationLoading,
    constellationOpen,
    toggleConstellation,
    derivedGroupBy,
    derivedFilterBy,
  };
}
